import {
	type CertificationPreview,
	type Profession,
	UserAction,
} from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import clsx from "clsx";
import { ChevronRightIcon } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import posthog from "posthog-js";
import { useEffect, useState } from "react";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";

import { OnboardingWizard } from "./OnboardingWizard";
import Dialog from "~/components/Dialog";
import { useOnboarding, useUserAction } from "~/hooks";
import useTrackCTA from "~/hooks/useTrackCTA";
import useUserFlags from "~/hooks/useUserFlags";
import {
	type CptStudyPlanPreset,
	resolveCptStudyPlanPreset,
} from "~/lib/cpt-study-plan-preset";
import {
	WELCOME_MODAL_OPTIONS,
	type WelcomeModalOption,
} from "~/lib/welcome-modal-options";

interface WelcomeOption extends WelcomeModalOption {
	action?: () => void;
}

const logButtonInteraction = (
	option: { key: string },
	callback: () => void,
) => {
	posthog.capture(`welcome_modal:${option.key}_clicked`);
	callback();
};

const WelcomeModal = () => {
	const [internalIsOpen, setInternalIsOpen] = useState(false);
	const { userAction, resetUserAction, setUserAction } = useUserAction();
	const trackCTA = useTrackCTA();

	const isOpen = internalIsOpen || userAction === UserAction.WELCOME;

	const [view, setView] = useState<"welcome" | "wizard">("welcome");
	const [preset, setPreset] = useState<CptStudyPlanPreset | null>(null);

	const { isSignedIn } = useAuth();

	const { updateFlag } = useUserFlags();
	const { setStep, step, setLearningPath, onboarding } = useOnboarding();

	// Same keys as useProfessions / useCertifications, fetched only while open.
	const { data: professions } = useSWRImmutable<Profession[]>(
		isOpen ? "/professions" : null,
	);
	const { data: certifications } = useSWR<CertificationPreview[]>(
		isOpen ? "/certifications" : null,
	);

	useEffect(() => {
		if (userAction === UserAction.WELCOME) {
			posthog.capture("welcome_modal:opened", { trigger: "user_action" });
		}
	}, [userAction]);

	const handleClose = async () => {
		if (isSignedIn) {
			await updateFlag({
				isWelcomeModalShown: true,
			});
		}
		setView("welcome");
		setStep("welcome");
		setPreset(null);
		setInternalIsOpen(false);
		if (userAction === UserAction.WELCOME) {
			await resetUserAction();
		}
	};

	useEffect(() => {
		if (view === "welcome" && step === "auth") {
			handleClose();
		}
	}, [view]);

	const logCloseInteraction = async () => {
		posthog.capture("welcome_modal:close_clicked", {
			view,
			wizard_step: view === "wizard" ? step : null,
		});
		await handleClose();
	};

	const launchWizard = () => {
		posthog.capture("welcome_modal:wizard_launched");
		setPreset(null);
		setLearningPath(null);
		setStep("profession");
		setView("wizard");
	};

	const cptStudyPlanPreset = resolveCptStudyPlanPreset({
		professions,
		certifications,
		savedPrimaryProfessionId: onboarding?.primaryProfessionId,
	});

	const launchCptStudyPlanWizard = (nextPreset: CptStudyPlanPreset) => {
		posthog.capture("welcome_modal:wizard_launched", {
			preset: "cpt_study_plan",
		});
		setPreset(nextPreset);
		setLearningPath("certification");
		setStep("auth");
		setView("wizard");
	};

	const options: WelcomeOption[] = WELCOME_MODAL_OPTIONS.map((option) => {
		if (option.key === "membership") {
			return {
				...option,
				action: () => {
					trackCTA(UserAction.SIGN_UP, "Welcome Modal");
					setUserAction(UserAction.SIGN_UP);
				},
			};
		}

		if (option.key === "learning_plan") {
			return {
				...option,
				action: () => launchWizard(),
			};
		}

		// Falls back to the study plan page link until its data has loaded.
		if (option.key === "cpt_study_plan" && cptStudyPlanPreset) {
			return {
				...option,
				action: () => launchCptStudyPlanWizard(cptStudyPlanPreset),
			};
		}

		return option;
	});

	return (
		<Dialog
			isOpen={isOpen}
			onClose={logCloseInteraction}
			className={clsx(
				"overflow-hidden",
				view === "wizard"
					? "flex max-h-[90vh] max-w-2xl flex-col md:max-h-[85vh]"
					: "max-w-xl",
			)}
		>
			<div
				id="welcome-modal"
				className={clsx(
					view === "wizard" && "flex h-full min-h-0 flex-1 flex-col",
				)}
			>
				{view === "wizard" ? (
					<OnboardingWizard
						mode="deferred"
						preset={preset}
						onExit={() => {
							setView("welcome");
							setStep("welcome");
							setPreset(null);
						}}
					/>
				) : (
					<div className="flex w-full flex-col items-center px-2 pb-2">
						<div className="mb-4 flex size-16 items-center justify-center rounded-2xl dark:bg-slate-800">
							<Image
								priority
								fetchPriority="high"
								src={`${process.env.NEXT_PUBLIC_BLOB_STORAGE_URL}/brookbush-institute-logo-icon-primary.png`}
								alt="Brookbush Institute Logo"
								width={64}
								height={64}
								className="size-24 object-contain"
							/>
						</div>

						<h2 className="w-70 text-center font-bold text-2xl text-slate-900 md:w-[320px] dark:text-white">
							Welcome to the Brookbush Institute
						</h2>

						<div className="mt-8 flex w-full max-w-lg flex-col gap-3">
							<p className="mb-1 font-bold text-slate-400 text-xs uppercase tracking-wider dark:text-slate-500">
								Recommended next steps
							</p>

							{options.map((option) => {
								const className =
									"group flex w-full items-center justify-between rounded-xl border border-slate-600 bg-white p-4 transition-all hover:border-slate-700 hover:shadow-sm dark:border-slate-600 dark:bg-slate-800 dark:hover:border-slate-500 dark:hover:bg-slate-700/50";

								const content = (
									<>
										<div className="flex items-center gap-4">
											<div
												className={clsx(
													"flex size-10 shrink-0 items-center justify-center rounded-full",
													option.bg,
												)}
											>
												<option.icon className={clsx("size-5", option.color)} />
											</div>
											<div className="flex flex-col text-left">
												<span className="font-semibold text-slate-900 transition-colors group-hover:text-primary dark:text-white">
													{option.title}
												</span>
											</div>
										</div>
										<ChevronRightIcon className="size-5 text-slate-400 transition-colors group-hover:text-slate-600 dark:group-hover:text-slate-300" />
									</>
								);

								return option.action ? (
									<button
										type="button"
										key={option.key}
										onClick={() => logButtonInteraction(option, option.action!)}
										className={className}
									>
										{content}
									</button>
								) : (
									<Link
										key={option.key}
										href={option.href}
										onClick={() => logButtonInteraction(option, handleClose)}
										className={className}
									>
										{content}
									</Link>
								);
							})}
						</div>
					</div>
				)}
			</div>
		</Dialog>
	);
};

export default WelcomeModal;
